/**
 * **Promo** — daftar per brand + sakelar aktif **per outlet**.
 *
 * ⛔ Yang BUKAN di layar ini: voucher GoFood/Grab. Uangnya ditagihkan ke penyelenggara ⇒ ia
 * metode bayar (piutang), bukan potongan pendapatan kita. Bukti kenapa pemisahan itu penting: di
 * VTEC satu boolean memikul tiga kategori ekonomi sekaligus.
 *
 * ⚠️ Sakelar per outlet menulis **pengecualian**, bukan mengubah promonya. Kalau ia menyentuh
 * promonya sendiri, mematikan promo di satu mal akan mematikannya di seluruh jaringan.
 *
 * ### ⭐ Bentuknya kini `CrudPanel` (Imron, 2026-08-03)
 * Formulir sebaris yang lama hanya bisa MEMBUAT. Promo yang salah nilai — dan itu yang paling
 * mahal — cuma bisa dimatikan lalu dibuat ulang dengan kode lain, sampai daftar promo penuh
 * bangkai `HEMAT10`, `HEMAT10B`, `HEMAT10-FIX`.
 */
import {
  Alert, DatePicker, Form, Input, InputNumber, Select, Space, Switch, Tag, TimePicker, Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import { PromoTargets } from '../components/PromoTargets';
import type { Outlet, Promo, PromoBlock } from '../api/client';
import { canWrite } from '../api/session';
import { BrandPicker } from '../components/BrandPicker';
import { CrudPanel } from '../components/CrudPanel';
import { FormTabs } from '../components/FormTabs';

function rupiah(kanonik: string): string {
  const bulat = kanonik.split('.')[0] ?? '0';
  return `Rp ${bulat.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
}

function nilai(p: Pick<Promo, 'kind' | 'percent_rate' | 'amount'>): string {
  if (p.kind === 'percent') {
    const persen = Number(p.percent_rate ?? '0') * 100;
    return `${String(Math.round(persen * 100) / 100)}%`;
  }
  return rupiah(p.amount ?? '0');
}

export function Promos() {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [brandId, setBrandId] = useState<string | null>(null);
  const [outlet, setOutlet] = useState<readonly Outlet[]>([]);
  const [outletId, setOutletId] = useState<string | null>(null);
  const [block, setBlock] = useState<readonly PromoBlock[]>([]);
  /* ⚠️ Pemicu muat-ulang daftar: angka status yang tak ikut berubah membuat orang menekan sakelarnya
     dua kali, dan yang kedua justru mengembalikannya ke keadaan semula. */
  const [tick, setTick] = useState(0);

  useEffect(() => { void api.outlets().then(setOutlet).catch(() => undefined); }, []);

  const muatBlock = useCallback(() => {
    if (outletId === null) { setBlock([]); return; }
    void api.promoBlocks(outletId).then(setBlock).catch(() => undefined);
  }, [outletId]);
  useEffect(() => { muatBlock(); }, [muatBlock]);

  /* ⛔ `includeInactive`: layar pengelola. Promo yang dimatikan harus tetap bisa dinyalakan lagi. */
  const muatPromo = useCallback(async (): Promise<readonly Promo[]> => {
    if (brandId === null) return [];
    return api.promos(brandId, true);
  }, [brandId]);

  const ubah = async (promoId: string, aktif: boolean) => {
    if (outletId === null) return;
    try {
      const ada = block.find((b) => b.promo_id === promoId);
      if (aktif) {
        if (ada !== undefined) await api.unblockPromo(ada.id);
      } else if (ada === undefined) {
        await api.blockPromo({ outletId, promoId });
      }
      muatBlock();
    } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    }
  };

  const kolom: ColumnsType<Promo> = [
    {
      title: t('promo.code'),
      dataIndex: 'code',
      width: 150,
      render: (v: string) => <b data-testid={`promo-${v}`}>{v}</b>,
    },
    { title: t('promo.name'), dataIndex: 'name' },
    { title: t('promo.scope'), dataIndex: 'scope', render: (v: string) => <Tag>{v}</Tag> },
    { title: t('promo.value'), key: 'nilai', width: 110, render: (_: unknown, p: Promo) => nilai(p) },
    {
      title: t('promo.cap'),
      dataIndex: 'max_discount',
      width: 130,
      render: (v: string | null) => (v === null ? '—' : rupiah(v)),
    },
    {
      /*
       * ⭐⭐ **Sakelar sungguhan, bukan lencana baca-saja** (Imron, 2026-08-06: *"switch on off
       * belum ada"*). Sebelumnya status global cuma bisa diubah dari DALAM laci sunting — tiga
       * ketukan untuk perbuatan yang paling sering dilakukan pada promo: mematikannya.
       *
       * ⚠️ Ini status GLOBAL (seluruh brand). Kolom sebelahnya mematikan promo pada SATU outlet
       * saja — dua hal berbeda yang sengaja berdampingan supaya bedanya terlihat.
       */
      title: t('outlets.status'),
      dataIndex: 'is_active',
      width: 110,
      render: (v: boolean, p: Promo) => (
        <span
          data-testid={`promo-aktif-${p.code}`}
          data-on={String(v)}
          /* ⛔ Barisnya juga bisa diklik untuk menyunting — tanpa ini satu ketukan memicu dua maksud. */
          onClick={(e) => { e.stopPropagation(); }}
        >
          <Switch
            size="small"
            checked={v}
            disabled={!boleh}
            onChange={(on) => {
              void (async () => {
                try {
                  await api.setStatus('promos', p.id, on);
                  setTick((n) => n + 1);
                } catch (e) {
                  message.error(e instanceof Error ? e.message : String(e));
                }
              })();
            }}
          />
        </span>
      ),
    },
    {
      title: t('promo.perOutlet'),
      key: 'aktif',
      width: 140,
      render: (_: unknown, p: Promo) => {
        if (outletId === null) return <Typography.Text type="secondary">—</Typography.Text>;
        const mati = block.some((b) => b.promo_id === p.id);
        return (
          /*
           * ⛔ `stopPropagation`: barisnya juga bisa diklik untuk menyunting. Tanpa ini, mematikan
           * promo di satu outlet akan SEKALIGUS membuka laci sunting promonya — dua maksud yang
           * berbeda jauh, dipicu satu ketukan.
           */
          <span
            data-testid={`sakelar-promo-${p.code}`}
            data-on={String(!mati)}
            onClick={(e) => { e.stopPropagation(); }}
          >
            <Switch size="small" checked={!mati} disabled={!boleh} onChange={(v) => void ubah(p.id, v)} />
          </span>
        );
      },
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="promos">
      <Space wrap>
        <BrandPicker value={brandId} onChange={setBrandId} />
        <Typography.Text type="secondary">{t('promo.perOutlet')}</Typography.Text>
        <span data-testid="pilih-outlet">
          <Select
            style={{ width: 240 }}
            placeholder={t('availability.outlet')}
            value={outletId}
            onChange={setOutletId}
            options={outlet.map((o) => ({ value: o.id, label: `${o.code} · ${o.name}` }))}
          />
        </span>
      </Space>
      <Alert type="info" showIcon title={t('promo.note')} data-testid="promo-catatan" />
      {outletId === null && (
        <Alert type="warning" showIcon title={t('promo.pickOutlet')} data-testid="promo-pilih-outlet" />
      )}

      <CrudPanel<Promo>
        testId="promo"
        historyPath="promos"
        title={t('promo.tab')}
        subtitle={t('promo.subtitle')}
        canWrite={boleh}
        newLabel={t('promo.add')}
        columns={kolom}
        fetchList={muatPromo}
        reloadToken={tick}
        searchIn={(p) => `${p.code} ${p.name}`}
        formWidth={520}
        blockedReason={brandId === null ? t('structure.pickBrand') : null}
        renderFields={(_f, editing) => <FieldPromo editing={editing} />}
        toFormValues={(p) => ({
          name: p.name,
          kind: p.kind,
          scope: p.scope,
          nilai: p.kind === 'percent'
            ? Math.round(Number(p.percent_rate ?? '0') * 10000) / 100
            : Number((p.amount ?? '0').split('.')[0]),
          cap: p.max_discount === null ? null : Number(p.max_discount.split('.')[0]),
          minSubtotal: p.min_subtotal === null ? null : Number(p.min_subtotal.split('.')[0]),
          requiresApproval: p.requires_approval,
          isActive: p.is_active,
          /* ⚠️ AntD menuntut `dayjs`; server menyimpan & menuntut string. Konversinya di SATU
             tempat (di sini dan `berlakuPromo`), bukan tersebar di tiap field. */
          validFrom: p.valid_from === null ? null : dayjs(p.valid_from),
          validTo: p.valid_to === null ? null : dayjs(p.valid_to),
          validTimeFrom: p.valid_time_from === null ? null : dayjs(p.valid_time_from, 'HH:mm:ss'),
          validTimeTo: p.valid_time_to === null ? null : dayjs(p.valid_time_to, 'HH:mm:ss'),
          validDays: p.valid_days,
        })}
        onCreate={async (v) => {
          if (brandId === null) return;
          await api.createPromo({
            brandId,
            code: String(v['code']).toUpperCase(),
            name: v['name'],
            kind: v['kind'],
            scope: v['scope'],
            ...nilaiPromo(v),
            ...berlakuPromo(v),
          });
        }}
        onUpdate={async (id, v, lama) => {
          /* ⛔ `kind` & `scope` TIDAK ikut: server memang menolaknya (`update` di registry tak
             punya keduanya). Nilai persen yang berubah jadi nominal akan menabrak CHECK
             `promo_value_matches_kind` — dan pesanan lama yang memakainya jadi tak terjelaskan. */
          await api.updateMaster('promos', id, {
            name: v['name'], ...nilaiPromo({ ...v, kind: lama.kind }), ...berlakuPromo(v),
          });
          if (v['isActive'] !== lama.is_active) {
            await api.setStatus('promos', id, v['isActive'] === true);
          }
        }}
      />
    </Space>
  );
}

/**
 * Nilai promo → dua kolom terpisah, sesuai `promo_value_matches_kind`.
 *
 * ⚠️ **RATE berskala 6, MONEY berskala 4** — dikirim sebagai string apa adanya. Membiarkannya jadi
 * float membuat 10% berkeliaran sebagai 0,09999999999999999, dan selisihnya baru terlihat saat
 * total sehari dijumlahkan.
 */
function nilaiPromo(v: Record<string, unknown>): Record<string, unknown> {
  const persen = v['kind'] === 'percent';
  const angka = Number(v['nilai'] ?? 0);
  const cap = v['cap'];
  return {
    percentRate: persen ? (angka / 100).toFixed(6) : null,
    amount: persen ? null : `${String(Math.round(angka))}.0000`,
    maxDiscount: cap == null || cap === '' ? null : `${String(Math.round(Number(cap)))}.0000`,
    /* ⛔ `null` DIKIRIM EKSPLISIT saat dikosongkan — sama alasannya dengan `berlakuPromo`:
       menghilangkannya dari body berarti "jangan ubah", dan minimum yang dihapus orang akan
       tetap berlaku diam-diam. */
    minSubtotal:
      v['minSubtotal'] == null || v['minSubtotal'] === ''
        ? null
        : `${String(Math.round(Number(v['minSubtotal'])))}.0000`,
    /* ⚠️ `=== true` bukan truthiness: `undefined` dari Switch yang tak pernah disentuh harus jadi
       `false` yang TEGAS, bukan lolos sebagai "biarkan apa adanya". */
    requiresApproval: v['requiresApproval'] === true,
  };
}

/**
 * Masa berlaku → bentuk yang diterima server.
 *
 * ⛔ `null` DIKIRIM EKSPLISIT saat dikosongkan, bukan dihilangkan dari body: menghilangkannya
 * berarti "jangan ubah", dan promo yang tanggal akhirnya dihapus orang akan tetap memegang tanggal
 * lamanya — perubahan yang terlihat tersimpan di layar tapi tak pernah sampai.
 *
 * ⚠️ Jam dikirim `HH:mm` (server menerima `HH:MM` maupun `HH:MM:SS`); tanggal `YYYY-MM-DD` LOKAL,
 * bukan `toISOString()` yang bergeser satu hari di UTC+7 setiap sebelum pukul 07:00.
 */
function berlakuPromo(v: Record<string, unknown>): Record<string, unknown> {
  const tgl = (x: unknown): string | null =>
    x == null ? null : (x as { format: (f: string) => string }).format('YYYY-MM-DD');
  const jam = (x: unknown): string | null =>
    x == null ? null : (x as { format: (f: string) => string }).format('HH:mm');
  const hari = v['validDays'];
  return {
    validFrom: tgl(v['validFrom']),
    validTo: tgl(v['validTo']),
    validTimeFrom: jam(v['validTimeFrom']),
    validTimeTo: jam(v['validTimeTo']),
    validDays: Array.isArray(hari) && hari.length > 0 ? hari : null,
  };
}

/** Field promo — komponen sungguhan supaya `Form.useWatch` menuruti rules-of-hooks. */
function FieldPromo({ editing }: { readonly editing: Promo | null }) {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const kind = Form.useWatch('kind', form) as string | undefined;
  const scope = Form.useWatch('scope', form) as string | undefined;
  const persen = (kind ?? editing?.kind ?? 'percent') === 'percent';

  return (
    <FormTabs
      testId="promo"
      items={[
        {
          key: 'identitas',
          label: t('outlets.tabIdentity'),
          names: ['code', 'name'],
          children: (
            <>
              {editing === null ? (
                <Form.Item
                  name="code"
                  label={t('promo.code')}
                  rules={[{ required: true, pattern: /^[A-Za-z0-9_-]+$/, message: t('structure.codeRule') }]}
                >
                  <Input maxLength={40} data-testid="promo-kode" />
                </Form.Item>
              ) : (
                <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t('promo.frozen')} />
              )}
              <Form.Item name="name" label={t('promo.name')} rules={[{ required: true, min: 2 }]}>
                <Input maxLength={120} data-testid="promo-nama" />
              </Form.Item>
            </>
          ),
        },
        /*
         * ⭐⭐⭐ **Sasaran promo — HANYA muncul untuk `scope: line`.**
         *
         * ⛔ Sebelum ini pilihan `line` di dropdown tak ada bedanya dari `order`: mesin membaca
         * `promo_target`, dan **tanpa satu baris pun target ia berlaku ke SEMUA baris** yang boleh
         * didiskon. Jadi "diskon 20% khusus minuman" tak bisa dibuat siapa pun — pilihan yang ada
         * di layar tapi tak berakibat apa-apa adalah setelan yang berbohong kepada penyetelnya.
         *
         * ⚠️ Tab ini disembunyikan pada `scope: order` dengan sengaja: target pada promo se-bill
         * tak dibaca mesin sama sekali, dan menampilkannya mengundang orang mengisinya lalu
         * mengira diskonnya menyempit.
         */
        ...(editing !== null && (scope ?? editing.scope) === 'line'
          ? [{
              key: 'sasaran',
              label: t('promo.tabTarget'),
              names: [] as string[],
              children: <PromoTargets promoId={editing.id} />,
            }]
          : []),
        {
          key: 'berlaku',
          label: t('promo.tabValidity'),
          names: ['validFrom', 'validTo', 'validTimeFrom', 'validTimeTo', 'validDays'],
          children: (
            <>
              {/*
                ⭐⭐⭐ **Masa berlaku sudah DITEGAKKAN mesin sejak lama** (`promo.ts`: `belum
                berlaku` · `sudah lewat masa berlakunya` · `tidak berlaku di hari ini` · jam) —
                yang tak ada cuma tempat menyetelnya.

                ⛔ Kontrol yang bekerja tapi tak bisa disetel siapa pun adalah kontrol yang
                dimatikan dengan cara lain: promo lebaran yang tak bisa diberi tanggal akhir akan
                dinonaktifkan tangan — kalau ada yang ingat. Kalau tidak, ia jalan terus.
              */}
              <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t('promo.validityHint')} />
              <Space>
                <Form.Item name="validFrom" label={t('promo.validFrom')}>
                  <DatePicker style={{ width: 170 }} data-testid="promo-mulai" />
                </Form.Item>
                <Form.Item name="validTo" label={t('promo.validTo')}>
                  <DatePicker style={{ width: 170 }} data-testid="promo-sampai" />
                </Form.Item>
              </Space>

              {/* ⭐ Jam berlaku: happy hour 15:00–18:00 adalah bentuk promo paling lazim di F&B. */}
              <Space>
                <Form.Item name="validTimeFrom" label={t('promo.timeFrom')}>
                  <TimePicker format="HH:mm" style={{ width: 170 }} data-testid="promo-jam-mulai" />
                </Form.Item>
                <Form.Item name="validTimeTo" label={t('promo.timeTo')}>
                  <TimePicker format="HH:mm" style={{ width: 170 }} data-testid="promo-jam-sampai" />
                </Form.Item>
              </Space>

              {/* ⚠️ 1 = Senin … 7 = Minggu — SAMA dengan `promo.ts`, bukan penomoran JS (0 = Minggu). */}
              <Form.Item name="validDays" label={t('promo.days')} tooltip={t('promo.daysHint')}>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder={t('promo.daysAll')}
                  data-testid="promo-hari"
                  options={[1, 2, 3, 4, 5, 6, 7].map((d) => ({
                    value: d, label: t(`promo.day.${String(d)}`),
                  }))}
                />
              </Form.Item>
            </>
          ),
        },
        {
          key: 'nilai',
          label: t('promo.tabValue'),
          names: ['kind', 'scope', 'nilai', 'cap', 'minSubtotal', 'requiresApproval', 'isActive'],
          children: (
            <>
              {/* ⛔ Jenis & cakupan hanya saat MEMBUAT: keduanya sudah ikut tercatat di pesanan
                  yang lewat. */}
              <Space>
                <Form.Item name="kind" label={t('promo.kind')} initialValue="percent">
                  {/* ⚠️ `data-testid` TIDAK diteruskan AntD Select — ujinya memeriksa
                      `.ant-select-disabled` di dalam laci. */}
                  <Select
                    style={{ width: 130 }}
                    disabled={editing !== null}
                    options={[{ value: 'percent', label: '%' }, { value: 'amount', label: 'Rp' }]}
                  />
                </Form.Item>
                <Form.Item name="scope" label={t('promo.scope')} initialValue="order">
                  <Select
                    style={{ width: 130 }}
                    disabled={editing !== null}
                    options={[{ value: 'order', label: 'order' }, { value: 'line', label: 'line' }]}
                  />
                </Form.Item>
              </Space>

              <Form.Item name="nilai" label={t('promo.value')} rules={[{ required: true }]}>
                <InputNumber
                  min={0}
                  /* ⭐ Persen dibatasi 100 di layar: "diskon 150%" adalah uang yang dikembalikan. */
                  {...(persen ? { max: 100 } : {})}
                  style={{ width: '100%' }}
                  addonAfter={persen ? '%' : 'Rp'}
                  data-testid="promo-nilai"
                />
              </Form.Item>

              {/* ⭐ Batas potongan hanya bermakna untuk persen — nominal membatasi dirinya sendiri. */}
              {persen && (
                <Form.Item name="cap" label={t('promo.cap')} tooltip={t('promo.capHint')}>
                  <InputNumber min={0} style={{ width: '100%' }} addonAfter="Rp"
                               data-testid="promo-cap" />
                </Form.Item>
              )}

              {/*
                ⭐ **Belanja minimum** — sudah DITEGAKKAN mesin (`PROMO-409-MIN_SUBTOTAL`) sejak
                promo dibuat, tanpa pernah punya tempat disetel.

                ⚠️ Dasarnya adalah baris yang MEMENUHI SYARAT, bukan total bill: rokok dan barang
                ber-`discount_allowed = false` tak ikut dihitung. Kalau ini tak disebut, "minimal
                belanja 100rb" akan dibaca kasir sebagai angka di layar bayar — dan ia akan
                mengira sistemnya salah saat bill 120rb ditolak karena 40rb-nya rokok.
              */}
              <Form.Item name="minSubtotal" label={t('promo.minSubtotal')}
                         tooltip={t('promo.minSubtotalHint')}>
                <InputNumber min={0} style={{ width: '100%' }} addonAfter="Rp"
                             data-testid="promo-min" />
              </Form.Item>

              {/*
                ⭐⭐ **Butuh persetujuan penyelia** — bukan tanda pengingat, melainkan kontrol yang
                MEMAKSA sejak 2026-08-06: kasir yang menerapkannya diminta kode & PIN penyelia
                ber-izin `promo:approve`, dan penyetujunya tercatat di bill.

                ⛔ Sebelum ini kolomnya ADA, dibaca mesin, lalu diabaikan — manajer yang
                menyalakannya mendapat promo yang berperilaku persis seperti tak ditandai.
              */}
              <Form.Item name="requiresApproval" label={t('promo.needsApproval')}
                         valuePropName="checked" tooltip={t('promo.needsApprovalHint')}>
                <Switch data-testid="promo-persetujuan" />
              </Form.Item>

              {editing !== null && (
                <>
                  <Form.Item name="isActive" label={t('outlets.status')} valuePropName="checked">
                    <Switch
                      data-testid="promo-aktif"
                      checkedChildren={t('outlets.active')}
                      unCheckedChildren={t('outlets.inactive')}
                    />
                  </Form.Item>
                  <Alert type="warning" showIcon title={t('promo.activeVsOutlet')} />
                </>
              )}
            </>
          ),
        },
      ]}
    />
  );
}
